import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { DashboardFilters } from '@oxxean/types';
import { fetchFilterOptions } from '../lib/api/dashboard';
import { queryKeys } from '../lib/query-client';
import { readSessionJson, SESSION_KEYS, writeSessionJson } from '../lib/storage';
import type { FilterOptions } from '../types/domain';
import { useAuth } from './useAuth';

export type FilterKey = keyof DashboardFilters;

/**
 * Filtros globales (§25).
 *
 * Persistidos en `sessionStorage`: sobreviven a la navegación y al recargado de
 * la pestaña, pero no se heredan en una sesión nueva —un filtro olvidado es una
 * de las causas más frecuentes de leer un número equivocado—.
 */
interface FiltersContextValue {
  filters: DashboardFilters;
  setFilter: <K extends FilterKey>(key: K, value: DashboardFilters[K]) => void;
  clearFilters: () => void;
  activeCount: number;
  options: FilterOptions | undefined;
  optionsLoading: boolean;
  optionsError: unknown;
  reloadOptions: () => void;
}

const FiltersContext = createContext<FiltersContextValue | null>(null);

const EMPTY_FILTERS: DashboardFilters = {};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readStoredFilters(): DashboardFilters {
  const stored = readSessionJson<unknown>(SESSION_KEYS.filters);
  return isPlainObject(stored) ? (stored as DashboardFilters) : EMPTY_FILTERS;
}

export function FiltersProvider({ children }: { children: ReactNode }): JSX.Element {
  const [filters, setFilters] = useState<DashboardFilters>(readStoredFilters);

  useEffect(() => {
    writeSessionJson(SESSION_KEYS.filters, filters);
  }, [filters]);

  // Los catálogos requieren sesión: pedirlos sin ella sólo generaría un 401.
  const { user } = useAuth();

  const optionsQuery = useQuery({
    queryKey: queryKeys.filterOptions,
    queryFn: fetchFilterOptions,
    enabled: user !== null,
    staleTime: 15 * 60_000,
  });

  const setFilter = useCallback(<K extends FilterKey>(key: K, value: DashboardFilters[K]) => {
    setFilters((current) => {
      const next: DashboardFilters = { ...current };
      if (value === undefined || value === null || value === '') delete next[key];
      else next[key] = value;
      return next;
    });
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
  }, []);

  const reloadOptions = useCallback(() => {
    void optionsQuery.refetch();
  }, [optionsQuery]);

  const value = useMemo<FiltersContextValue>(
    () => ({
      filters,
      setFilter,
      clearFilters,
      activeCount: Object.keys(filters).length,
      options: optionsQuery.data,
      optionsLoading: optionsQuery.isLoading,
      optionsError: optionsQuery.isError ? optionsQuery.error : null,
      reloadOptions,
    }),
    [
      filters,
      setFilter,
      clearFilters,
      optionsQuery.data,
      optionsQuery.isLoading,
      optionsQuery.isError,
      optionsQuery.error,
      reloadOptions,
    ],
  );

  return <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>;
}

export function useFilters(): FiltersContextValue {
  const context = useContext(FiltersContext);
  if (!context) throw new Error('useFilters debe usarse dentro de <FiltersProvider>.');
  return context;
}
